/**
 * PURPOSE: Seleccionar participantes sin que el menú quede recortado por el modal.
 * RESPONSIBILITIES: Buscar por institución/grupo, mostrar identidad visual y evitar selecciones duplicadas.
 * COLLABORATORS: MatchPlannerModal.jsx y react-select.
 * ROUTE: winscore/src/area/FasesArea/components/ParticipantSelect.jsx
 */
import React, { useMemo } from "react";
import Select, { components as SelectComponents } from "react-select";

const UNDEFINED_VALUE = "__participant_undefined__";

const normalize = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const initials = (participant) =>
  (participant?.institution?.abrev || participant?.institution?.name || "--")
    .slice(0, 2)
    .toUpperCase();

const getGroupLabel = (participant) => {
  if (!participant?.idgrupo) return "Sin grupo";
  if (participant.group?.name) return participant.group.name;
  return `Grupo ${participant.group?.nrogrupo || participant.idgrupo}`;
};

const ParticipantIdentity = ({ participant, compact = false }) => {
  if (!participant) {
    return <span className="font-bold text-slate-500">Por definir</span>;
  }

  const institution = participant.institution || {};
  return (
    <span className="flex min-w-0 items-center gap-2">
      <span
        className={`relative flex shrink-0 items-center justify-center overflow-hidden rounded-lg bg-slate-100 font-black text-slate-500 ${
          compact ? "h-8 w-8 text-[9px]" : "h-9 w-9 text-[10px]"
        }`}
      >
        {initials(participant)}
        {institution.logoUrl ? (
          <img
            src={institution.logoUrl}
            alt=""
            className="absolute inset-0 h-full w-full bg-white object-contain p-0.5"
            onError={(event) => {
              event.currentTarget.style.display = "none";
            }}
          />
        ) : null}
      </span>
      <span className="min-w-0">
        <span className="block truncate text-sm font-black text-slate-800">
          {institution.abrev ||
            institution.name ||
            `Participante ${participant.idparticipant}`}
        </span>
        {!compact ? (
          <span className="block truncate text-[11px] font-semibold text-slate-500">
            {institution.name || "Institución"} · {getGroupLabel(participant)}
          </span>
        ) : null}
      </span>
    </span>
  );
};

const ParticipantOption = (props) => (
  <SelectComponents.Option {...props}>
    <ParticipantIdentity participant={props.data.participant} />
  </SelectComponents.Option>
);

const ParticipantSingleValue = (props) => (
  <SelectComponents.SingleValue {...props}>
    <ParticipantIdentity participant={props.data.participant} compact />
  </SelectComponents.SingleValue>
);

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 46,
    borderRadius: 12,
    borderColor: state.isFocused ? "#3b82f6" : "#cbd5e1",
    boxShadow: state.isFocused ? "0 0 0 2px #dbeafe" : "none",
    "&:hover": { borderColor: "#60a5fa" },
  }),
  valueContainer: (base) => ({ ...base, padding: "3px 8px" }),
  menu: (base) => ({ ...base, minWidth: 320, overflow: "hidden" }),
  menuPortal: (base) => ({ ...base, zIndex: 1800 }),
  option: (base, state) => ({
    ...base,
    padding: "8px 10px",
    backgroundColor: state.isSelected
      ? "#dbeafe"
      : state.isFocused
      ? "#eff6ff"
      : "#ffffff",
    color: "#0f172a",
    cursor: "pointer",
  }),
  singleValue: (base) => ({ ...base, maxWidth: "calc(100% - 4px)" }),
  placeholder: (base) => ({ ...base, color: "#94a3b8", fontWeight: 600 }),
};

const ParticipantSelect = ({
  value,
  onChange,
  options = [],
  allowUndefined = false,
  excludedValues = [],
  disabled = false,
  placeholder = "Seleccionar participante",
}) => {
  const excluded = useMemo(
    () => new Set(excludedValues.map(Number).filter(Boolean)),
    [excludedValues]
  );

  const selectOptions = useMemo(() => {
    const mapped = options
      .filter(
        (participant) =>
          !excluded.has(Number(participant.idparticipant)) ||
          Number(participant.idparticipant) === Number(value)
      )
      .map((participant) => {
        const groupLabel = getGroupLabel(participant);
        const institution = participant.institution || {};
        return {
          value: Number(participant.idparticipant),
          participant,
          label:
            institution.abrev ||
            institution.name ||
            `Participante ${participant.idparticipant}`,
          search: normalize(
            `${institution.name || ""} ${institution.abrev || ""} ${
              participant.idparticipant
            } ${participant.idgrupo || ""} ${groupLabel}`
          ),
        };
      });

    return allowUndefined
      ? [
          {
            value: UNDEFINED_VALUE,
            participant: null,
            label: "Por definir",
            search: "por definir pendiente",
          },
          ...mapped,
        ]
      : mapped;
  }, [allowUndefined, excluded, options, value]);

  const selected = useMemo(() => {
    if (!value && allowUndefined) {
      return selectOptions.find((option) => option.value === UNDEFINED_VALUE);
    }
    return selectOptions.find(
      (option) => Number(option.value) === Number(value)
    );
  }, [allowUndefined, selectOptions, value]);

  return (
    <Select
      value={selected || null}
      onChange={(option) =>
        onChange(option?.value === UNDEFINED_VALUE ? "" : option?.value || "")
      }
      options={selectOptions}
      isDisabled={disabled}
      isSearchable
      placeholder={placeholder}
      noOptionsMessage={() => "Sin coincidencias"}
      filterOption={(candidate, inputValue) =>
        candidate.data.search.includes(normalize(inputValue))
      }
      components={{
        Option: ParticipantOption,
        SingleValue: ParticipantSingleValue,
      }}
      styles={selectStyles}
      menuPortalTarget={
        typeof document !== "undefined" ? document.body : undefined
      }
      menuPosition="fixed"
      menuPlacement="auto"
      classNamePrefix="participant-select"
    />
  );
};

export default ParticipantSelect;
